/**
 * Rating Buttons Component - Frontend component for rating AI responses
 * 
 * This component provides user-friendly rating buttons for AI responses
 * and handles the communication with the backend rating system.
 */

"use client"

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ThumbsUp, ThumbsDown, MessageSquare, Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getApiUrl } from '@/lib/api-config'
import { Textarea } from '@/components/ui/textarea';

interface RatingButtonsProps {
  messageId: string
  userId: string
  onRatingSubmitted?: (rating: 'good' | 'bad', ratingId: string) => void
  className?: string
}

interface DetailedFeedbackProps {
  messageId: string
  userId: string
  onClose: () => void
  onSubmit: (feedback: DetailedFeedbackData) => void
}

interface DetailedFeedbackData {
  rating: number
  feedbackType: string
  feedbackText: string
  isGoodResponse: boolean
}

export function RatingButtons({ 
  messageId, 
  userId, 
  onRatingSubmitted,
  className 
}: RatingButtonsProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDetailedFeedback, setShowDetailedFeedback] = useState(false)
  const [hasRated, setHasRated] = useState(false)

  const handleRating = async (isGood: boolean) => {
    if (isSubmitting || hasRated) return

    setIsSubmitting(true)

    try {
      const response = await fetch(getApiUrl('api/ai-feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message_id: messageId,
          is_good_response: isGood,
          rating: isGood ? 5 : 1 // Convert thumbs up/down to 1-5 rating
        })
      })

      if (!response.ok) {
        throw new Error('Failed to submit rating')
      }

      const data = await response.json()
      
      if (data.success) {
        setHasRated(true)
        onRatingSubmitted?.(isGood ? 'good' : 'bad', data.rating_id)
      } else {
        throw new Error(data.error || 'Failed to submit rating')
      }

    } catch (error) {
      console.error('Error submitting rating:', error)
      // You might want to show a toast notification here
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDetailedFeedback = () => {
    setShowDetailedFeedback(true)
  }

  const handleDetailedSubmit = (feedback: DetailedFeedbackData) => {
    setHasRated(true)
    setShowDetailedFeedback(false)
    onRatingSubmitted?.(feedback.isGoodResponse ? 'good' : 'bad', 'detailed')
  }

  if (hasRated) {
    return (
      <div className={cn("flex items-center gap-2 text-sm text-green-600", className)}>
        <Star className="h-4 w-4 fill-current" />
        <span>Thank you for your feedback!</span>
      </div>
    )
  }

  if (showDetailedFeedback) {
    return (
      <DetailedFeedback
        messageId={messageId}
        userId={userId}
        onClose={() => setShowDetailedFeedback(false)}
        onSubmit={handleDetailedSubmit}
      />
    )
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleRating(true)}
        disabled={isSubmitting}
        className="flex items-center gap-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-gray-300"
        title="Good Response"
      >
        <ThumbsUp className="h-4 w-4" />
      </Button>
      
      <Button
        size="sm"
        variant="outline"
        onClick={() => handleRating(false)}
        disabled={isSubmitting}
        className="flex items-center gap-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-gray-300"
        title="Needs Improvement"
      >
        <ThumbsDown className="h-4 w-4" />
      </Button>
      
      <Button
        size="sm"
        variant="outline"
        onClick={handleDetailedFeedback}
        disabled={isSubmitting}
        className="flex items-center gap-1 text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-gray-300"
        title="Detailed Feedback"
      >
        <MessageSquare className="h-4 w-4" />
      </Button>
    </div>
  )
}

function DetailedFeedback({ 
  messageId, 
  userId, 
  onClose, 
  onSubmit 
}: DetailedFeedbackProps) {
  const [rating, setRating] = useState(3)
  const [feedbackType, setFeedbackType] = useState('helpful')
  const [feedbackText, setFeedbackText] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      const response = await fetch(getApiUrl('api/ai-feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message_id: messageId,
          rating: rating,
          feedback_type: feedbackType,
          feedback_text: feedbackText,
          is_good_response: rating >= 3
        })
      })

      if (!response.ok) {
        throw new Error('Failed to submit detailed feedback')
      }

      const data = await response.json()
      
      if (data.success) {
        onSubmit({
          rating,
          feedbackType,
          feedbackText,
          isGoodResponse: rating >= 3
        })
      } else {
        throw new Error(data.error || 'Failed to submit feedback')
      }

    } catch (error) {
      console.error('Error submitting detailed feedback:', error)
      // You might want to show a toast notification here
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mt-2 p-4 border rounded-lg bg-gray-50">
      <h4 className="text-sm font-medium mb-3">Provide Detailed Feedback</h4>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Star Rating */}
        <div>
          <label className="text-sm font-medium mb-2 block">Rating</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className={cn(
                  "text-2xl transition-colors",
                  star <= rating ? "text-yellow-400" : "text-gray-300"
                )}
              >
                ★
              </button>
            ))}
            <span className="ml-2 text-sm text-gray-600">{rating}/5</span>
          </div>
        </div>

        {/* Feedback Type */}
        <div>
          <label className="text-sm font-medium mb-2 block">What was this feedback about?</label>
          <select
            value={feedbackType}
            onChange={(e) => setFeedbackType(e.target.value)}
            className="w-full p-2 border rounded-md text-sm"
          >
            <option value="helpful">Helpfulness</option>
            <option value="accurate">Accuracy</option>
            <option value="relevant">Relevance</option>
            <option value="complete">Completeness</option>
          </select>
        </div>

        {/* Feedback Text */}
        <div>
          <label className="text-sm font-medium mb-2 block">Additional Comments (Optional)</label>
          <Textarea
            compact
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Tell us more about your experience..."
            rows={3}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            type="submit"
            size="sm"
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
          
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  )
}

// Export default component
export default RatingButtons

